import { useEffect, useMemo, useState } from 'react'
import { Fingerprint, Nfc, UserX, UserCheck, Link2, Link2Off, ClipboardList, ChevronRight, Smartphone, KeyRound, Trash2, Car } from 'lucide-react'
import { Shell, useShellMeta } from '../admin/Shell.js'
import { Link, navigate } from '../router.js'
import { SmartBack } from '../navigation/SmartBack.js'
import { Select, useToast, adminMutate, usePermissions } from '../admin/primitives.js'
import { useSaveBar } from '../shell/editing.js'
import { useConfirm } from '../shell/confirm.js'
import {
  useApi, useAccessCaps, actionInfo, ActionButton, runCommand, AccessModal,
  tr, PERSON_STATUS, CRED_STATUS, CRED_TYPE, RESULT_LABELS, METHOD_LABELS, dateTime, PersonAvatar,
} from './lib.js'
import { useMenuAcces } from './menu-acces.js'

/**
 * FICHE PERSONNE ACCESS (§36-60, §88-94) — une vraie page d'administration :
 * identité ÉDITABLE (Save Bar, diff réel), statut, liaison RH fonctionnelle
 * (suggestions ≠ auto-link), règles cliquables avec PROVENANCE, accès
 * configurés (jamais « peut ouvrir maintenant »), identifiants humanisés avec
 * actions réelles, activité + historique complet. Chaque action indisponible
 * dit exactement pourquoi.
 */

interface Fiche {
  id: string; firstName: string | null; lastName: string | null; fullName: string; alias: string | null
  photoUrl: string | null
  email: string | null; phone: string | null; employeeNumber: string | null; status: string
  nfcCount: number; hasPin: boolean; hasTouchPass: boolean
  policies: { id: string; name: string; origin: 'DIRECT' | 'GROUP' }[]
  groups: { id: string; name: string }[]
  personId: string | null; personName: string | null
  onboardTime: number | null
  nfcCards: { displayId: string | null; alias: string | null; nfcId: string | null; status: string | null; type: string | null }[]
  touchPasses: { id: string; name: string | null; status: string | null }[]
  accessConfig: { doorId: string | null; door: string; policyId: string; policy: string; origin: string; schedule: string | null }[]
  licensePlates: { id: string | null; number: string | null }[]
  activity: { at: string | null; door: string | null; method: string | null; result: string | null }[]
  activityTotal: number
}

type Identity = { firstName: string; lastName: string; alias: string; email: string; phone: string; employeeNumber: string }
const identityOf = (f: Fiche): Identity => ({
  firstName: f.firstName ?? '', lastName: f.lastName ?? '', alias: f.alias ?? '',
  email: f.email ?? '', phone: f.phone ?? '', employeeNumber: f.employeeNumber ?? '',
})

export function AccessPersonPage({ id }: { id: string }) {
  const meta = useShellMeta()
  const toast = useToast()
  const confirm = useConfirm()
  const caps = useAccessCaps()
  const perms = usePermissions()
  const menu = useMenuAcces()
  const { data: f, reload } = useApi<Fiche>(`/api/v1/access/people/${id}`)
  const [busy, setBusy] = useState<string | null>(null)
  const [linkOpen, setLinkOpen] = useState(false)
  const [polOpen, setPolOpen] = useState(false)
  const [cardOpen, setCardOpen] = useState(false)
  const [tpOpen, setTpOpen] = useState(false)
  const [platesOpen, setPlatesOpen] = useState(false)
  const [pinOnce, setPinOnce] = useState<string | null>(null)

  /* ─── identité éditable : diff réel avec le snapshot serveur ─── */
  const [draft, setDraft] = useState<Identity | null>(null)
  const server = useMemo(() => (f ? identityOf(f) : null), [f])
  useEffect(() => { if (server) setDraft(server) }, [server])
  const canEdit = actionInfo(caps, 'UPDATE_USER')?.status === 'AVAILABLE'
  const editInfo = actionInfo(caps, 'UPDATE_USER')
  const dirty = !!(canEdit && draft && server && JSON.stringify(draft) !== JSON.stringify(server))
  const sensitiveMasked = f?.email === '•••'

  useSaveBar({
    dirty,
    saveLabel: 'Enregistrer dans UniFi Access',
    onSave: async () => {
      if (!draft || !server || !f) return 'Rien à enregistrer.'
      const user: Record<string, unknown> = {}
      if (draft.firstName !== server.firstName) user.first_name = draft.firstName
      if (draft.lastName !== server.lastName) user.last_name = draft.lastName
      if (draft.alias !== server.alias) user.alias = draft.alias
      if (draft.email !== server.email) user.user_email = draft.email
      if (draft.phone !== server.phone) user.phone = draft.phone
      if (draft.employeeNumber !== server.employeeNumber) user.employee_number = draft.employeeNumber
      const r = await runCommand('UPDATE_USER', { user_id: f.id, user })
      if (!r.ok) return r.error ?? 'Refusé.'
      await new Promise((res) => setTimeout(res, 1600)) // resync ciblée du miroir
      reload()
      return true
    },
    onCancel: () => { if (server) setDraft(server) },
    successMessage: 'Modifié dans UniFi Access.',
  })

  const setStatus = async (status: 'ACTIVE' | 'DEACTIVATED') => {
    if (!f) return
    const verb = status === 'ACTIVE' ? 'Réactiver' : 'Désactiver'
    if (!(await confirm({ title: `${verb} l’accès de ${f.fullName} ?`, message: status === 'DEACTIVATED' ? 'Ses identifiants cesseront immédiatement de fonctionner dans UniFi Access.' : 'Ses identifiants fonctionneront à nouveau dans UniFi Access.', variant: 'sensitive', confirmLabel: verb }))) return
    setBusy('status')
    const r = await runCommand('SET_STATUS', { user_id: f.id, status })
    setBusy(null)
    if (!r.ok) { toast('error', r.error ?? 'Refusé.'); return }
    toast('ok', `${verb === 'Réactiver' ? 'Accès réactivé' : 'Accès désactivé'} dans UniFi Access.`)
    setTimeout(reload, 1600)
  }

  const unlink = async () => {
    if (!f || !(await confirm({ title: 'Retirer l’association RH ?', message: `${f.fullName} ne sera plus lié à l’employé ${f.personName}. Ni le compte Access ni le dossier RH ne sont modifiés.`, confirmLabel: 'Délier' }))) return
    const r = await adminMutate<{ error?: string; message?: string }>('DELETE', `/api/v1/access/people/${f.id}/link`)
    if (r.status >= 400) { toast('error', r.data.message ?? 'Refusé.'); return }
    toast('ok', 'Association retirée.')
    reload()
  }

  const canLink = !perms.loading && perms.permissions.includes('access.people.link_rh')

  /* ─── PIN (§109) : généré côté serveur, montré UNE seule fois, jamais stocké ─── */
  const assignPin = async () => {
    if (!f || !(await confirm({ title: `Attribuer un PIN à ${f.fullName} ?`, message: 'UniFi Access génère le code — il sera affiché UNE SEULE fois, puis jamais réaffiché.', variant: 'sensitive', confirmLabel: 'Générer le PIN' }))) return
    setBusy('pin')
    const r = await runCommand('ACCESS_PIN_ASSIGN', { user_id: f.id })
    setBusy(null)
    if (!r.ok) { toast('error', r.error ?? 'Refusé.'); return }
    setPinOnce((r.result as { pin_code?: string } | null)?.pin_code ?? null)
    setTimeout(reload, 1600)
  }
  const removePin = async () => {
    if (!f || !(await confirm({ title: `Retirer le PIN de ${f.fullName} ?`, message: 'Il ne pourra plus ouvrir par code.', variant: 'sensitive', confirmLabel: 'Retirer le PIN' }))) return
    setBusy('pin')
    const r = await runCommand('ACCESS_PIN_UNASSIGN', { user_id: f.id })
    setBusy(null)
    if (!r.ok) { toast('error', r.error ?? 'Refusé.'); return }
    toast('ok', 'PIN retiré dans UniFi Access.')
    setTimeout(reload, 1600)
  }

  /* ─── SUPPRESSION (§63, §104) — récapitulatif d'impact, jamais le dossier RH ─── */
  const deletePerson = async () => {
    if (!f) return
    const impact = [`${f.nfcCards.length} carte(s) NFC`, `${f.touchPasses.length} Touch Pass`, `${f.policies.length} règle(s)`, f.personId ? `liaison RH avec ${f.personName} (le dossier RH n'est PAS supprimé)` : 'aucune liaison RH'].join(', ')
    if (!(await confirm({ title: `Supprimer ${f.fullName} d’UniFi Access ?`, message: `Suppression DÉFINITIVE. Impact : ${impact}.`, variant: 'critical', confirmLabel: 'Supprimer' }))) return
    if (!(await confirm({ title: 'Dernière confirmation', message: `Supprimer définitivement ${f.fullName} ? Cette action est irréversible.`, variant: 'critical', confirmLabel: 'Supprimer définitivement' }))) return
    setBusy('delete')
    const r = await runCommand('DELETE_USER', { user_id: f.id })
    setBusy(null)
    if (!r.ok) { toast('error', r.error ?? 'Refusé.'); return }
    toast('ok', `${f.fullName} supprimé d’UniFi Access. Le dossier RH éventuel est intact.`)
    setTimeout(() => navigate('/securite/controle-acces/personnes'), 1200)
  }

  return (
    <Shell active="security" meta={meta}>
      <main className="lc-page lc-stagger ac-page">
        <SmartBack fallback="/securite/controle-acces/personnes" />
        {f ? (
          <>
            <header className="ac-head ac-phead" {...menu.personne({ id: f.id, nom: f.fullName || f.alias || null, personId: f.personId, personName: f.personName, email: f.email })}>
              <div className="ac-phead__id">
                <PersonAvatar name={f.fullName} photoUrl={f.photoUrl} size="lg" />
                <div>
                  <h1 className="lc-title">{f.fullName || f.alias || '—'}</h1>
                  <p className="lc-subtitle ac-phead__meta">
                    <span className={`ac-status ac-status--${f.status.toLowerCase()}`}>{tr(PERSON_STATUS, f.status)}</span>
                    <span className="ac-typechip">{f.personId ? 'Employé lié' : 'Personne Access'}</span>
                    {/* onboard_time=0 = epoch provider, pas une vraie date d'arrivée. */}
                    {f.onboardTime && f.onboardTime > 86_400 ? <span className="lc-hint">Depuis le {new Date(f.onboardTime * 1000).toLocaleDateString('fr-FR')}</span> : null}
                  </p>
                </div>
              </div>
              {/* Actions principales VISIBLES (§93-94) — pas cachées dans « … ». */}
              <div className="ac-actions">
                <ActionButton caps={caps} action="ACCESS_NFC_ASSIGN" label="Attribuer un badge" icon={<Nfc size={14} aria-hidden="true" />} onRun={() => setCardOpen(true)} />
                <ActionButton caps={caps} action="ACCESS_TOUCHPASS_ASSIGN" label="Attribuer Touch Pass" icon={<Smartphone size={14} aria-hidden="true" />} onRun={() => setTpOpen(true)} />
                <ActionButton caps={caps} action="ASSIGN_POLICIES" label="Modifier les accès" icon={<ClipboardList size={14} aria-hidden="true" />} onRun={() => setPolOpen(true)} />
                {f.status === 'ACTIVE'
                  ? <ActionButton caps={caps} action="SET_STATUS" label="Désactiver l’accès" icon={<UserX size={14} aria-hidden="true" />} busy={busy === 'status'} onRun={() => void setStatus('DEACTIVATED')} />
                  : <ActionButton caps={caps} action="SET_STATUS" label="Réactiver l’accès" primary icon={<UserCheck size={14} aria-hidden="true" />} busy={busy === 'status'} onRun={() => void setStatus('ACTIVE')} />}
              </div>
            </header>

            <div className="ac-detail-grid">
              <section className="ac-panel">
                <h2 className="lc-section-title">Identité</h2>
                {canEdit && draft && !sensitiveMasked ? (
                  <div className="ac-idform">
                    {([['firstName', 'Prénom'], ['lastName', 'Nom'], ['alias', 'Alias'], ['email', 'E-mail'], ['phone', 'Téléphone'], ['employeeNumber', 'Matricule']] as [keyof Identity, string][]).map(([k, lbl]) => (
                      <label key={k} className="ac-field">{lbl}
                        <input value={draft[k]} onChange={(e) => setDraft({ ...draft, [k]: e.target.value })} />
                      </label>
                    ))}
                    <p className="lc-hint">Les modifications sont écrites dans UniFi Access (barre d’enregistrement en bas).</p>
                  </div>
                ) : (
                  <>
                    <dl className="ac-dl">
                      <div><dt>Prénom / Nom</dt><dd>{[f.firstName, f.lastName].filter(Boolean).join(' ') || '—'}</dd></div>
                      <div><dt>Alias</dt><dd>{f.alias ?? '—'}</dd></div>
                      <div><dt>E-mail</dt><dd>{f.email ?? '—'}</dd></div>
                      <div><dt>Téléphone</dt><dd>{f.phone ?? '—'}</dd></div>
                      <div><dt>Matricule</dt><dd>{f.employeeNumber ?? '—'}</dd></div>
                    </dl>
                    {!canEdit && editInfo?.reason ? <p className="ac-blockhint">🔒 {editInfo.reason}</p> : null}
                    {canEdit && sensitiveMasked ? <p className="ac-blockhint">🔒 Édition indisponible : votre rôle ne voit pas les coordonnées complètes.</p> : null}
                  </>
                )}
              </section>

              <section className="ac-panel">
                <h2 className="lc-section-title">Liaison Gestion</h2>
                {f.personId ? (
                  <div className="ac-linkstate">
                    <p><Fingerprint size={14} aria-hidden="true" /> Lié à l’employé <strong>{f.personName}</strong>.</p>
                    <p className="ac-linkactions">
                      <Link href={`/equipe/rh/salaries/${f.personId}`} className="ac-seeall">Voir le dossier RH →</Link>
                      {canLink ? <button type="button" className="lc-btn lc-btn--ghost ac-btn-sm" onClick={() => void unlink()}><Link2Off size={13} aria-hidden="true" /> Délier</button> : null}
                    </p>
                    <p className="lc-hint">Délier ne modifie ni le compte Access ni le dossier RH — seulement la relation dans Gestion.</p>
                  </div>
                ) : canLink ? (
                  <div className="ac-linkstate">
                    <p className="lc-hint">Aucune identité Gestion liée.</p>
                    <button type="button" className="lc-btn" onClick={() => setLinkOpen(true)}><Link2 size={14} aria-hidden="true" /> Associer à un employé</button>
                  </div>
                ) : (
                  <p className="ac-blockhint">🔒 Votre rôle ne permet pas d’associer une personne Access à un employé.</p>
                )}
              </section>

              <section className="ac-panel">
                <h2 className="lc-section-title">Règles d’accès</h2>
                <div className="ac-chips">
                  {f.policies.map((p) => (
                    <Link key={p.id} href={`/securite/controle-acces/regles/${p.id}`} className="ac-chip ac-chip--link">
                      {p.name}<em className="ac-chip__origin">{p.origin === 'DIRECT' ? 'directe' : 'via groupe'}</em>
                    </Link>
                  ))}
                  {f.policies.length === 0 ? <span className="lc-hint">Aucune règle.</span> : null}
                </div>
                <h3 className="ac-subtitle">Groupes</h3>
                <div className="ac-chips">
                  {f.groups.map((g) => (
                    <Link key={g.id} href={`/securite/controle-acces/personnes?groupe=${g.id}`} className="ac-chip ac-chip--group ac-chip--link" title="Voir les membres du groupe">{g.name}</Link>
                  ))}
                  {f.groups.length === 0 ? <span className="lc-hint">Aucun groupe.</span> : null}
                </div>
                <p className="lc-hint">L’affectation aux groupes n’est pas exposée par l’API de cette version d’UniFi Access — elle se gère dans la console.</p>
              </section>

              <section className="ac-panel">
                <h2 className="lc-section-title">Accès configurés</h2>
                {f.accessConfig.length > 0 ? (
                  <ul className="ac-config">
                    {f.accessConfig.map((c, i) => (
                      <li key={i} className="ac-config__row">
                        {c.doorId ? <Link href={`/securite/controle-acces/portes/${c.doorId}`} className="ac-config__door" {...menu.porte({ id: c.doorId, nom: c.door })}>{c.door}</Link> : <span className="ac-config__door">{c.door}</span>}
                        <span className="ac-config__meta">{c.policy}{c.origin === 'GROUP' ? ' (via groupe)' : ''}{c.schedule ? ` · ${c.schedule}` : ''}</span>
                      </li>
                    ))}
                  </ul>
                ) : <p className="lc-hint">Aucune porte couverte par les règles de cette personne.</p>}
                <p className="ac-cover-note">Portes COUVERTES PAR LES RÈGLES — pas une garantie d’ouverture instantanée (horaires, urgence et état des portes s’appliquent).</p>
              </section>

              <section className="ac-panel">
                <h2 className="lc-section-title">Identifiants</h2>
                <ul className="ac-credcards">
                  {f.nfcCards.map((c, i) => (
                    <li key={i}>
                      <Link href={`/securite/controle-acces/identifiants/${c.displayId ?? c.nfcId}`} className="ac-credcard">
                        <Nfc size={16} aria-hidden="true" />
                        <span className="ac-credcard__id"><strong>{c.alias || `Carte ${c.displayId}`}</strong><em>{tr(CRED_TYPE, c.type)} · ID {c.displayId}</em></span>
                        <span className={`ac-status ac-status--${(c.status ?? '').toLowerCase()}`}>{tr(CRED_STATUS, c.status)}</span>
                        <ChevronRight size={14} aria-hidden="true" />
                      </Link>
                    </li>
                  ))}
                  {f.touchPasses.map((t) => (
                    <li key={t.id}>
                      <Link href={`/securite/controle-acces/identifiants/tp:${t.id}`} className="ac-credcard">
                        <Smartphone size={16} aria-hidden="true" />
                        <span className="ac-credcard__id"><strong>{t.name || 'Touch Pass'}</strong><em>Touch Pass</em></span>
                        <span className={`ac-status ac-status--${(t.status ?? '').toLowerCase()}`}>{tr(CRED_STATUS, t.status)}</span>
                        <ChevronRight size={14} aria-hidden="true" />
                      </Link>
                    </li>
                  ))}
                  {f.nfcCards.length === 0 && f.touchPasses.length === 0 ? <li className="lc-hint">Aucun identifiant.</li> : null}
                </ul>
                <p className="ac-pinline"><KeyRound size={13} aria-hidden="true" /> PIN : {f.hasPin ? 'configuré (le code secret n’est jamais réaffiché)' : 'aucun'}
                  {f.hasPin
                    ? <ActionButton caps={caps} action="ACCESS_PIN_UNASSIGN" label="Retirer le PIN" busy={busy === 'pin'} onRun={() => void removePin()} />
                    : <ActionButton caps={caps} action="ACCESS_PIN_ASSIGN" label="Attribuer un PIN" busy={busy === 'pin'} onRun={() => void assignPin()} />}
                </p>
                <p className="ac-pinline"><Car size={13} aria-hidden="true" /> Plaques : {f.licensePlates.length > 0 ? f.licensePlates.map((p) => p.number).filter(Boolean).join(', ') : 'aucune'}
                  <ActionButton caps={caps} action="ACCESS_PLATE_ASSIGN" label="Gérer les plaques" onRun={() => setPlatesOpen(true)} />
                </p>
              </section>

              <section className="ac-panel ac-panel--wide">
                <div className="ac-cov-head">
                  <h2 className="lc-section-title">Activité récente</h2>
                  <Link href={`/securite/controle-acces/historique?personne=${f.id}&identifie=1`} className="ac-seeall">Voir tout l’historique ({f.activityTotal}) →</Link>
                </div>
                <ul className="ac-events">
                  {f.activity.map((e, i) => (
                    <li key={i} className={`ac-event ${e.result && e.result !== 'ACCESS' ? 'ac-event--denied' : ''}`}>
                      <span className="ac-event__time">{dateTime(e.at)}</span>
                      <span className="ac-event__door">{e.door ?? '—'}</span>
                      <span className="ac-event__method">{tr(METHOD_LABELS, e.method)}</span>
                      <span className="ac-event__result">{tr(RESULT_LABELS, e.result)}</span>
                    </li>
                  ))}
                  {f.activity.length === 0 ? <li className="lc-hint">Aucune activité.</li> : null}
                </ul>
              </section>
            </div>

            <p className="ac-dangerline">
              <ActionButton caps={caps} action="DELETE_USER" label="Supprimer cette personne d’UniFi Access" icon={<Trash2 size={13} aria-hidden="true" />} busy={busy === 'delete'} onRun={() => void deletePerson()} />
              <span className="lc-hint">Irréversible — un récapitulatif d’impact est présenté ; le dossier RH éventuel n’est jamais supprimé.</span>
            </p>

            {linkOpen ? <LinkModal accessId={f.id} accessName={f.fullName} onClose={(changed) => { setLinkOpen(false); if (changed) reload() }} /> : null}
            {polOpen ? <PoliciesModal fiche={f} onClose={(changed) => { setPolOpen(false); if (changed) setTimeout(reload, 1600) }} /> : null}
            {cardOpen ? <AssignCardModal userId={f.id} userName={f.fullName} onClose={(changed) => { setCardOpen(false); if (changed) setTimeout(reload, 1600) }} /> : null}
            {tpOpen ? <AssignTouchPassModal userId={f.id} userName={f.fullName} onClose={(changed) => { setTpOpen(false); if (changed) setTimeout(reload, 1600) }} /> : null}
            {platesOpen ? <PlatesModal fiche={f} onClose={(changed) => { setPlatesOpen(false); if (changed) setTimeout(reload, 1600) }} /> : null}
            {pinOnce !== null ? (
              <AccessModal title="PIN attribué" onClose={() => setPinOnce(null)} footer={<button type="button" className="lc-btn lc-btn--primary" onClick={() => setPinOnce(null)}>J’ai transmis le code</button>}>
                <p>Le PIN de <strong>{f.fullName}</strong> est :</p>
                <p className="ac-pin-once">{pinOnce || '—'}</p>
                <p className="lc-hint">Affiché UNE SEULE fois — Gestion ne le stocke pas et ne pourra jamais le réafficher.</p>
              </AccessModal>
            ) : null}
          </>
        ) : <p className="lc-hint">Chargement…</p>}
      </main>
    </Shell>
  )
}

/* ─────────── Associer à un employé (§45-49) — suggestion ≠ auto-link ─────────── */

function LinkModal({ accessId, accessName, onClose }: { accessId: string; accessName: string; onClose: (changed: boolean) => void }) {
  const toast = useToast()
  const [search, setSearch] = useState('')
  const [chosen, setChosen] = useState<{ personId: string; name: string } | null>(null)
  const [busy, setBusy] = useState(false)
  const { data } = useApi<{ suggestions: { personId: string; name: string; confidence: string; signals: string[] }[]; people: { personId: string; name: string }[] }>(`/api/v1/access/people/${accessId}/link-candidates${search ? `?search=${encodeURIComponent(search)}` : ''}`)

  const confirm = async () => {
    if (!chosen) return
    setBusy(true)
    const r = await adminMutate<{ error?: string; message?: string }>('POST', `/api/v1/access/people/${accessId}/link`, { personId: chosen.personId })
    setBusy(false)
    if (r.status >= 400) { toast('error', r.data.message ?? 'Refusé.'); return }
    toast('ok', `${accessName} associé à ${chosen.name}.`)
    onClose(true)
  }

  const CONF: Record<string, string> = { TRES_FORTE: 'Très forte', FORTE: 'Forte', A_VERIFIER: 'À vérifier' }
  return (
    <AccessModal title="Associer à un employé" onClose={() => onClose(false)} footer={
      <><button type="button" className="lc-btn" onClick={() => onClose(false)}>Annuler</button>
        <button type="button" className="lc-btn lc-btn--primary" disabled={!chosen || busy} onClick={() => void confirm()}>{busy ? 'Association…' : chosen ? `Associer à ${chosen.name}` : 'Choisir un employé'}</button></>
    }>
      <p className="lc-hint">Relie <strong>{accessName}</strong> à un dossier RH. Rien n’est modifié dans UniFi Access ni dans Skello — seulement la relation Gestion. Aucune association n’est jamais faite automatiquement.</p>
      {data && data.suggestions.length > 0 ? (
        <>
          <h3 className="ac-subtitle">Suggestions</h3>
          <ul className="ac-linklist">
            {data.suggestions.map((s) => (
              <li key={s.personId}>
                <button type="button" className={`ac-linkrow ${chosen?.personId === s.personId ? 'is-on' : ''}`} onClick={() => setChosen({ personId: s.personId, name: s.name })}>
                  <strong>{s.name}</strong>
                  <span className="lc-pill lc-pill--idle">{CONF[s.confidence] ?? s.confidence}</span>
                  <em>{s.signals.join(', ')}</em>
                </button>
              </li>
            ))}
          </ul>
        </>
      ) : null}
      <h3 className="ac-subtitle">Tous les employés</h3>
      <input className="ac-search" placeholder="Rechercher un employé…" value={search} onChange={(e) => setSearch(e.target.value)} />
      <ul className="ac-linklist">
        {(data?.people ?? []).map((p) => (
          <li key={p.personId}>
            <button type="button" className={`ac-linkrow ${chosen?.personId === p.personId ? 'is-on' : ''}`} onClick={() => setChosen(p)}><strong>{p.name}</strong></button>
          </li>
        ))}
      </ul>
    </AccessModal>
  )
}

/* ─────────── Modifier les accès (§55-56) — affectation de règles ─────────── */

function PoliciesModal({ fiche, onClose }: { fiche: Fiche; onClose: (changed: boolean) => void }) {
  const toast = useToast()
  const confirm = useConfirm()
  const { data } = useApi<{ policies: { id: string; name: string; scheduleName: string | null; doorCount: number }[] }>('/api/v1/access/policies')
  const direct = useMemo(() => new Set(fiche.policies.filter((p) => p.origin === 'DIRECT').map((p) => p.id)), [fiche])
  const inherited = useMemo(() => new Set(fiche.policies.filter((p) => p.origin === 'GROUP').map((p) => p.id)), [fiche])
  const [sel, setSel] = useState<Set<string>>(new Set(direct))
  const [busy, setBusy] = useState(false)

  const toggle = (id: string) => {
    const next = new Set(sel)
    if (next.has(id)) next.delete(id); else next.add(id)
    setSel(next)
  }
  const removed = data ? data.policies.filter((p) => direct.has(p.id) && !sel.has(p.id)) : []
  const added = data ? data.policies.filter((p) => !direct.has(p.id) && sel.has(p.id)) : []
  const dirty = removed.length > 0 || added.length > 0

  const save = async () => {
    // Impact AVANT d'enlever (§56) : ce que la personne perd, nommé.
    if (removed.length > 0 && !(await confirm({ title: `Retirer des accès à ${fiche.fullName} ?`, message: `Cela retire : ${removed.map((r) => `« ${r.name} » (${r.doorCount} porte(s)${r.scheduleName ? ', ' + r.scheduleName : ''})`).join(', ')}.`, variant: 'sensitive', confirmLabel: 'Retirer' }))) return
    setBusy(true)
    const r = await runCommand('ASSIGN_POLICIES', { user_id: fiche.id, access_policy_ids: [...sel] })
    setBusy(false)
    if (!r.ok) { toast('error', r.error ?? 'Refusé.'); return }
    toast('ok', 'Règles d’accès modifiées dans UniFi Access.')
    onClose(true)
  }

  return (
    <AccessModal title={`Règles d’accès — ${fiche.fullName}`} onClose={() => onClose(false)} footer={
      <><button type="button" className="lc-btn" onClick={() => onClose(false)}>Annuler</button>
        <button type="button" className="lc-btn lc-btn--primary" disabled={!dirty || busy} onClick={() => void save()}>{busy ? 'Enregistrement…' : 'Enregistrer dans UniFi Access'}</button></>
    }>
      <p className="lc-hint">Les affectations DIRECTES de cette personne. Les règles héritées d’un groupe ne se retirent pas ici.</p>
      <ul className="ac-pollist">
        {(data?.policies ?? []).map((p) => {
          const inh = inherited.has(p.id)
          return (
            <li key={p.id}>
              <label className={`ac-polrow ${inh ? 'is-inherited' : ''}`}>
                <input type="checkbox" checked={inh || sel.has(p.id)} disabled={inh} onChange={() => toggle(p.id)} />
                <span className="ac-polrow__name"><strong>{p.name}</strong><em>{p.doorCount} porte(s){p.scheduleName ? ` · ${p.scheduleName}` : ''}</em></span>
                {inh ? <span className="lc-pill lc-pill--idle">via groupe</span> : null}
              </label>
            </li>
          )
        })}
      </ul>
    </AccessModal>
  )
}

/* ─────────── Attribuer une carte NFC existante (§66) ─────────── */

function AssignCardModal({ userId, userName, onClose }: { userId: string; userName: string; onClose: (changed: boolean) => void }) {
  const toast = useToast()
  const { data } = useApi<{ nfc: { displayId: string | null; alias: string | null; status: string | null; owner: string | null }[] }>('/api/v1/access/credentials')
  const free = (data?.nfc ?? []).filter((c) => !c.owner)
  const [card, setCard] = useState('')
  const [busy, setBusy] = useState(false)

  const assign = async () => {
    if (!card) return
    setBusy(true)
    const r = await runCommand('ACCESS_NFC_ASSIGN', { card_id: card, user_id: userId, user_type: 'USER' })
    setBusy(false)
    if (!r.ok) { toast('error', r.error ?? 'Refusé.'); return }
    toast('ok', `Carte attribuée à ${userName} dans UniFi Access.`)
    onClose(true)
  }

  return (
    <AccessModal title={`Attribuer un badge — ${userName}`} onClose={() => onClose(false)} footer={
      <><button type="button" className="lc-btn" onClick={() => onClose(false)}>Annuler</button>
        <button type="button" className="lc-btn lc-btn--primary" disabled={!card || busy} onClick={() => void assign()}>{busy ? 'Attribution…' : 'Attribuer la carte'}</button></>
    }>
      {free.length > 0 ? (
        <>
          <p className="lc-hint">{free.length} carte(s) NFC non attribuée(s) dans l’inventaire.</p>
          <Select value={card} options={free.map((c) => ({ value: c.displayId ?? '', label: `${c.alias || 'Carte ' + c.displayId} · ID ${c.displayId}` }))} onChange={setCard} placeholder="Choisir une carte…" ariaLabel="Carte à attribuer" />
        </>
      ) : (
        <p className="lc-hint">Aucune carte NFC libre. <button type="button" className="ac-inline-link" onClick={() => { onClose(false); navigate('/securite/controle-acces/identifiants') }}>Enrôler une nouvelle carte →</button></p>
      )}
    </AccessModal>
  )
}

/* ─────────── Attribuer un Touch Pass (§3.25 officiel) ─────────── */

function AssignTouchPassModal({ userId, userName, onClose }: { userId: string; userName: string; onClose: (changed: boolean) => void }) {
  const toast = useToast()
  const { data } = useApi<{ touchPass: { id: string; name: string | null; status: string | null; user: string | null }[] }>('/api/v1/access/credentials')
  const free = (data?.touchPass ?? []).filter((t) => !t.user)
  const [tp, setTp] = useState('')
  const [busy, setBusy] = useState(false)
  const assign = async () => {
    if (!tp) return
    setBusy(true)
    const r = await runCommand('ACCESS_TOUCHPASS_ASSIGN', { user_id: userId, touch_pass_id: tp })
    setBusy(false)
    if (!r.ok) { toast('error', r.error ?? 'Refusé.'); return }
    toast('ok', `Touch Pass attribué à ${userName} dans UniFi Access.`)
    onClose(true)
  }
  return (
    <AccessModal title={`Attribuer un Touch Pass — ${userName}`} onClose={() => onClose(false)} footer={
      <><button type="button" className="lc-btn" onClick={() => onClose(false)}>Annuler</button>
        <button type="button" className="lc-btn lc-btn--primary" disabled={!tp || busy} onClick={() => void assign()}>{busy ? 'Attribution…' : 'Attribuer'}</button></>
    }>
      {free.length > 0 ? (
        <>
          <p className="lc-hint">{free.length} Touch Pass non attribué(s).</p>
          <Select value={tp} options={free.map((t) => ({ value: t.id, label: t.name || `Touch Pass ${t.id.slice(0, 8)}` }))} onChange={setTp} placeholder="Choisir un Touch Pass…" ariaLabel="Touch Pass à attribuer" />
        </>
      ) : <p className="lc-hint">Aucun Touch Pass libre. L’achat de nouveaux Touch Pass est différé (dépense réelle).</p>}
    </AccessModal>
  )
}

/* ─────────── Plaques d'immatriculation (§3.28/3.29 officiels) ─────────── */

function PlatesModal({ fiche, onClose }: { fiche: Fiche; onClose: (changed: boolean) => void }) {
  const toast = useToast()
  const confirm = useConfirm()
  const [toAdd, setToAdd] = useState('')
  const [busy, setBusy] = useState<string | null>(null)
  const add = async () => {
    const plates = toAdd.split(/[,;\n]/).map((x) => x.trim()).filter(Boolean)
    if (plates.length === 0) return
    setBusy('add')
    const r = await runCommand('ACCESS_PLATE_ASSIGN', { user_id: fiche.id, license_plates: plates })
    setBusy(null)
    if (!r.ok) { toast('error', r.error ?? 'Refusé.'); return }
    toast('ok', 'Plaque(s) attribuée(s) dans UniFi Access.')
    onClose(true)
  }
  const remove = async (p: { id: string | null; number: string | null }) => {
    if (!p.id || !(await confirm({ title: `Retirer la plaque ${p.number} ?`, message: 'Elle n’ouvrira plus les accès par lecture de plaque.', confirmLabel: 'Retirer' }))) return
    setBusy(p.id)
    const r = await runCommand('ACCESS_PLATE_REMOVE', { user_id: fiche.id, license_plate_id: p.id })
    setBusy(null)
    if (!r.ok) { toast('error', r.error ?? 'Refusé.'); return }
    toast('ok', 'Plaque retirée dans UniFi Access.')
    onClose(true)
  }
  return (
    <AccessModal title={`Plaques — ${fiche.fullName}`} onClose={() => onClose(false)} footer={
      <><button type="button" className="lc-btn" onClick={() => onClose(false)}>Fermer</button>
        <button type="button" className="lc-btn lc-btn--primary" disabled={!toAdd.trim() || busy === 'add'} onClick={() => void add()}>{busy === 'add' ? 'Attribution…' : 'Attribuer'}</button></>
    }>
      <h3 className="ac-subtitle">Plaques actuelles</h3>
      <ul className="ac-linklist">
        {fiche.licensePlates.map((p, i) => (
          <li key={i}><span className="ac-linkrow"><strong>{p.number ?? '—'}</strong>{p.id ? <button type="button" className="ac-inline-link" disabled={busy === p.id} onClick={() => void remove(p)}>Retirer</button> : <em>ID indisponible (resynchroniser)</em>}</span></li>
        ))}
        {fiche.licensePlates.length === 0 ? <li className="lc-hint">Aucune plaque.</li> : null}
      </ul>
      <h3 className="ac-subtitle">Ajouter</h3>
      <input className="ac-search" placeholder="AB-123-CD, EF-456-GH…" value={toAdd} onChange={(e) => setToAdd(e.target.value)} />
    </AccessModal>
  )
}
